import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { PageMeta } from '@/components/seo/page-meta'
import {
  NEWS_CATEGORY_LABELS,
  type NewsArticle,
  type NewsCategory,
  getNewsPath,
  listFeaturedNewsArticles,
  listNewsArticles,
} from '@/content/news/articles'
import { PELGO_NEWS_INDEX_SEO } from '@/lib/pelgo-seo'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

type FilterValue = 'all' | NewsCategory

const FILTERS: { value: FilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'company', label: NEWS_CATEGORY_LABELS.company },
  { value: 'press', label: NEWS_CATEGORY_LABELS.press },
]

function NewsCard({ article }: { article: NewsArticle }) {
  return (
    <Link
      to={getNewsPath(article.slug)}
      className="group flex flex-col rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="aspect-[16/10] overflow-hidden bg-gray-100">
        <img
          src={article.heroImageSrc}
          alt={article.heroImageAlt}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          loading="lazy"
        />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          {article.outletLogoSrc ? (
            <img
              src={article.outletLogoSrc}
              alt=""
              className="h-5 w-auto max-w-[100px] object-contain object-left"
            />
          ) : null}
          <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-pelgo-blue-light">
            {NEWS_CATEGORY_LABELS[article.category]}
          </span>
        </div>
        <h2 className="text-xl font-display text-gray-900 leading-snug tracking-[-0.4px] group-hover:text-pelgo-blue-light transition-colors">
          {article.title}
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed line-clamp-3">{article.summary}</p>
        <time dateTime={article.publishedAt} className="mt-auto text-xs text-gray-400">
          {article.displayDate}
        </time>
      </div>
    </Link>
  )
}

function FeaturedNewsCard({ article }: { article: NewsArticle }) {
  return (
    <Link
      to={getNewsPath(article.slug)}
      className="group block rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="grid md:grid-cols-2 gap-0">
        <div className="aspect-[16/10] md:aspect-auto md:min-h-[280px] overflow-hidden bg-gray-100">
          <img
            src={article.heroImageSrc}
            alt={article.heroImageAlt}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            loading="eager"
          />
        </div>
        <div className="flex flex-col justify-center gap-4 p-6 md:p-10">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-pelgo-blue-light">
              Featured
            </span>
            <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
              {NEWS_CATEGORY_LABELS[article.category]}
            </span>
          </div>
          {article.outletLogoSrc ? (
            <img
              src={article.outletLogoSrc}
              alt=""
              className="h-6 w-auto max-w-[120px] object-contain object-left"
            />
          ) : null}
          <h2 className="text-2xl md:text-3xl font-display text-gray-900 leading-snug tracking-[-0.6px] group-hover:text-pelgo-blue-light transition-colors">
            {article.title}
          </h2>
          <p className="text-base text-gray-500 leading-relaxed">{article.summary}</p>
          <time dateTime={article.publishedAt} className="text-sm text-gray-400">
            {article.displayDate}
          </time>
        </div>
      </div>
    </Link>
  )
}

export function NewsIndexPage() {
  const [filter, setFilter] = useState<FilterValue>('all')
  const articles = listNewsArticles()
  const featured = listFeaturedNewsArticles()[0]

  const filtered = useMemo(() => {
    const withoutFeatured = featured
      ? articles.filter((article) => article.slug !== featured.slug)
      : articles
    const pool =
      filter === 'all' ? withoutFeatured : withoutFeatured.filter((a) => a.category === filter)
    // When filtering, include featured in the grid if it matches
    if (filter !== 'all' && featured?.category === filter) {
      return [featured, ...pool]
    }
    return pool
  }, [articles, featured, filter])

  const showFeatured = filter === 'all' && featured

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PageMeta config={PELGO_NEWS_INDEX_SEO} />
      <MarketingNav variant="standalone" />
      <main className="container max-w-5xl px-4 pt-32 pb-16 space-y-10">
        <section className="space-y-3">
          <h1 className="text-4xl font-display font-medium text-gray-900 tracking-[-0.8px]">
            News
          </h1>
          <p className="text-gray-600 max-w-2xl">
            Company updates and media coverage from Pelgo — outplacement, severance, and the future
            of work.
          </p>
        </section>

        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter news by category">
          {FILTERS.map((item) => {
            const selected = filter === item.value
            return (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setFilter(item.value)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  selected
                    ? 'bg-pelgo-blue-light text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {item.label}
              </button>
            )
          })}
        </div>

        {showFeatured ? <FeaturedNewsCard article={featured} /> : null}

        {filtered.length > 0 ? (
          <section className="grid gap-8 md:grid-cols-2">
            {filtered.map((article) => (
              <NewsCard key={article.slug} article={article} />
            ))}
          </section>
        ) : showFeatured ? null : (
          <p className="text-gray-500 text-sm">No articles in this category yet.</p>
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
